import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { deleteEmissionsAllocation } from "api";
import { DeleteIconButton, DeleteDialog } from "components";
import { useNotify } from "hooks";

interface DeleteAllocationButtonProps {
  allocationId: number;
}

export const DeleteAllocationButton = ({
  allocationId,
}: DeleteAllocationButtonProps) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [isDeleteAllocationModalOpen, setIsDeleteAllocationModalOpen] =
    useState(false);

  const { mutateAsync, isLoading } = useMutation<
    void,
    AxiosError,
    { allocationId: number }
  >(({ allocationId: allocId }) => deleteEmissionsAllocation(allocId), {
    onSuccess: () => {
      notify.success("Emissions allocation setup successfully deleted.");
      queryClient.invalidateQueries(["emissionsAllocations"]);
      toggleDeleteAllocationModal();
    },
    onError: (error: AxiosError) => {
      notify.error(
        typeof error?.response?.data === "string"
          ? error.response.data
          : "Failed to delete emissions allocation setup",
      );
      toggleDeleteAllocationModal();
    },
  });

  const handleDelete = async () => {
    if (allocationId) {
      await mutateAsync({ allocationId });
    }
  };

  const toggleDeleteAllocationModal = () =>
    setIsDeleteAllocationModalOpen(!isDeleteAllocationModalOpen);

  return (
    <>
      <DeleteIconButton
        onClick={toggleDeleteAllocationModal}
        tooltipTitle="Delete"
      />
      {isDeleteAllocationModalOpen && (
        <DeleteDialog
          isOpen={isDeleteAllocationModalOpen}
          onCancel={toggleDeleteAllocationModal}
          onDeleteClick={handleDelete}
          target="EmissionsAllocationSetup"
          title="Delete Emissions Allocation Setup?"
          confirmButtonDisabled={isLoading}
          confirmTitle={isLoading ? "Deleting..." : "Delete"}
        />
      )}
    </>
  );
};
